import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Branch {
  id: string;
  name: string;
  code: string;
  address: string;
  city: string;
  region?: string;
  country: string;
  phone?: string;
  email?: string;
  manager_id?: string;
  is_active: boolean;
  operating_hours: Record<string, { open: string; close: string }>;
  settings: Record<string, unknown>;
  coordinates?: { lat: number; lng: number };
  description?: string;
  established_date?: string;
  created_at: string;
  updated_at: string;
}

export interface UserBranchAccess {
  branch_id: string;
  branch_name: string;
  branch_code: string;
  role: 'super_admin' | 'branch_admin' | 'branch_staff' | 'admin' | 'staff' | 'customer';
  is_primary: boolean;
  permissions: string[];
}

interface BranchContextType {
  // Current branch state
  currentBranch: Branch | null;
  setCurrentBranch: (branch: Branch | null) => void;

  // User's accessible branches
  accessibleBranches: UserBranchAccess[];
  isLoadingBranches: boolean;

  // Branch permissions
  hasPermission: (permission: string, branchId?: string) => boolean;
  canAccessBranch: (branchId: string) => boolean;
  isSuperAdmin: boolean;
  isBranchAdmin: (branchId?: string) => boolean;

  // Branch operations
  switchBranch: (branchId: string) => Promise<boolean>;
  refreshBranches: () => Promise<void>;

  // Branch data helpers
  getBranchById: (branchId: string) => Branch | null;
  getAccessibleBranchIds: () => string[];
}

const BranchContext = createContext<BranchContextType | undefined>(undefined);

interface BranchProviderProps {
  children: ReactNode;
}

export function BranchProvider({ children }: BranchProviderProps) {
  const { user, userRoles } = useAuth();
  const [currentBranch, setCurrentBranch] = useState<Branch | null>(null);
  const [accessibleBranches, setAccessibleBranches] = useState<UserBranchAccess[]>([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(true);
  const [allBranches, setAllBranches] = useState<Branch[]>([]);

  // Check if user is super admin
  const isSuperAdmin = userRoles?.some(role => role.role === 'super_admin') || false;

  // Check if user usually can access a specific branch
  const canAccessBranch = React.useCallback((branchId: string): boolean => {
    if (isSuperAdmin) return true;
    return accessibleBranches.some(access => access.branch_id === branchId);
  }, [isSuperAdmin, accessibleBranches]);

  // Load user's accessible branches
  const loadAccessibleBranches = React.useCallback(async () => {
    if (!user) {
      setAccessibleBranches([]);
      setCurrentBranch(null);
      setIsLoadingBranches(false);
      return;
    }

    try {
      setIsLoadingBranches(true);

      // Get user's accessible branches - use direct query to bypass auth context issues
      let branchAccess;

      // Always use direct query since the view depends on auth.uid() which may not work in all contexts
      console.log('Using direct query for branch access');
      const { data, error: directError } = await supabase
        .from('user_branch_access')
        .select(`
          branch_id,
          role,
          is_primary_branch,
          permissions,
          branches:branch_id (
            name,
            code
          )
        `)
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (data) {
        branchAccess = data.map((access: Record<string, unknown>) => {
          const branchData = Array.isArray(access.branches) ? (access.branches as unknown[])[0] : access.branches;
          return {
            branch_id: access.branch_id,
            branch_name: (branchData as Record<string, unknown>)?.name || 'Unknown',
            branch_code: (branchData as Record<string, unknown>)?.code || '',
            role: access.role,
            is_primary: access.is_primary_branch || false,
            permissions: access.permissions || []
          };
        });
      }

      if (directError) {
        console.error('Error loading branch access:', directError);
        return;
      }

      const formattedAccess: UserBranchAccess[] = branchAccess?.map((access: {
        branch_id: string;
        branch_name: string;
        branch_code: string;
        role: string;
        is_primary: boolean;
        permissions: string[];
      }) => ({
        branch_id: access.branch_id,
        branch_name: access.branch_name,
        branch_code: access.branch_code,
        role: access.role,
        is_primary: access.is_primary,
        permissions: access.permissions || []
      })) || [];

      setAccessibleBranches(formattedAccess);

      // Load full branch details for accessible branches
      if (formattedAccess.length > 0) {
        const branchIds = formattedAccess.map(access => access.branch_id);
        const { data: branches, error: branchError } = await supabase
          .from('branches')
          .select('*')
          .in('id', branchIds)
          .eq('is_active', true);

        if (branchError) {
          console.error('Error loading branch details:', branchError);
        } else {
          setAllBranches(branches || []);

          // Set primary branch as current if no current branch selected
          if (!currentBranch) {
            const primaryAccess = formattedAccess.find(access => access.is_primary);
            if (primaryAccess) {
              const primaryBranch = branches?.find(b => b.id === primaryAccess.branch_id);
              if (primaryBranch) {
                setCurrentBranch(primaryBranch);
                localStorage.setItem('selectedBranchId', primaryBranch.id);
              }
            } else if (branches && branches.length > 0) {
              // If no primary branch, select the first available branch
              setCurrentBranch(branches[0]);
              localStorage.setItem('selectedBranchId', branches[0].id);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error in loadAccessibleBranches:', error);
    } finally {
      setIsLoadingBranches(false);
    }
  }, [user, currentBranch]);

  // Load branches when user changes
  useEffect(() => {
    loadAccessibleBranches();
  }, [loadAccessibleBranches]);

  // Restore selected branch from localStorage
  useEffect(() => {
    const savedBranchId = localStorage.getItem('selectedBranchId');
    if (savedBranchId && allBranches.length > 0 && !currentBranch) {
      const savedBranch = allBranches.find(b => b.id === savedBranchId);
      if (savedBranch && canAccessBranch(savedBranchId)) {
        setCurrentBranch(savedBranch);
      }
    }
  }, [allBranches, currentBranch, canAccessBranch]);

  // Check if user is branch admin for a specific branch
  const isBranchAdmin = React.useCallback((branchId?: string): boolean => {
    if (isSuperAdmin) return true;

    const targetBranchId = branchId || currentBranch?.id;
    if (!targetBranchId) return false;

    return accessibleBranches.some(access =>
      access.branch_id === targetBranchId &&
      ['branch_admin', 'admin'].includes(access.role)
    );
  }, [isSuperAdmin, currentBranch, accessibleBranches]);

  // Check if user has specific permission
  const hasPermission = React.useCallback((permission: string, branchId?: string): boolean => {
    if (isSuperAdmin) return true;

    const targetBranchId = branchId || currentBranch?.id;
    if (!targetBranchId) return false;

    const branchAccess = accessibleBranches.find(access => access.branch_id === targetBranchId);
    if (!branchAccess) return false;

    // Branch admins have all permissions
    if (['super_admin', 'admin', 'branch_admin'].includes(branchAccess.role)) {
      return true;
    }

    // Check specific permissions
    return branchAccess.permissions.includes(permission);
  }, [isSuperAdmin, currentBranch, accessibleBranches]);

  // Switch to a different branch
  const switchBranch = async (branchId: string): Promise<boolean> => {
    if (!canAccessBranch(branchId)) {
      console.error('User does not have access to branch:', branchId);
      return false;
    }

    const branch = allBranches.find(b => b.id === branchId);
    if (!branch) {
      console.error('Branch not found:', branchId);
      return false;
    }

    setCurrentBranch(branch);
    localStorage.setItem('selectedBranchId', branchId);
    return true;
  };

  // Get branch by ID
  const getBranchById = (branchId: string): Branch | null => {
    return allBranches.find(b => b.id === branchId) || null;
  };

  // Get accessible branch IDs
  const getAccessibleBranchIds = (): string[] => {
    return accessibleBranches.map(access => access.branch_id);
  };

  // Refresh branches data
  const refreshBranches = async (): Promise<void> => {
    await loadAccessibleBranches();
  };

  const value: BranchContextType = {
    currentBranch,
    setCurrentBranch,
    accessibleBranches,
    isLoadingBranches,
    hasPermission,
    canAccessBranch,
    isSuperAdmin,
    isBranchAdmin,
    switchBranch,
    refreshBranches,
    getBranchById,
    getAccessibleBranchIds,
  };

  return (
    <BranchContext.Provider value={value}>
      {children}
    </BranchContext.Provider>
  );
}

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (context === undefined) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
};

// Helper hook for branch-filtered queries
export const useBranchFilter = () => {
  const { currentBranch, getAccessibleBranchIds, isSuperAdmin } = useBranch();

  return {
    branchId: currentBranch?.id,
    branchIds: getAccessibleBranchIds(),
    shouldFilterByBranch: !isSuperAdmin,
    getBranchFilter: () => {
      if (isSuperAdmin) return {}; // No filter for super admins
      if (currentBranch) return { branch_id: currentBranch.id };
      return { branch_id: { in: getAccessibleBranchIds() } };
    }
  };
};